import { useEffect } from 'react'
import { NextPage } from 'next'
import { useQuery } from '@apollo/client'

import {
  AuctionByArtworkQuery,
  AuctionByArtworkQueryVariables,
  GetEventAuctionByIdQuery,
  UserOffersQuery,
} from 'gql'
import OpenSeaAssetCard from 'components/nft/opensea-asset'
import TimeLeft from 'components/auction/time-left'
import CurrentBidContainer from 'components/auction/current-bid'
import PlaceBid from 'components/auction/place-bid'
import DescBlock from 'components/auction/common/DescBlock'
import SubmitForGiveway from 'components/auction/submit-for-giveway'

import usePusher from 'hooks/auction'
import { SlideShowGallery, MetaTags, Tabs, EditLink, notifyError } from '~shared'
import { Grid, GlowHeading, Typography, Link } from '~ui'
import TwoColumnLayout from '~ui/Layouts/TwoColumnLayout'
import { getEventMetaData, getEventSchema } from '~utils/seo'
import { formatDateTime, getImageUrl, textToTitleCase } from '~utils'

import { useQueryParams } from '~hooks'
import { GET_USER_OFFERS } from 'gql/user/queries'
import { GET_AUCTION_BY_ARTWORK } from 'gql/artwork/queries'
import styles from './auction.module.scss'
import ConditionsOfSale from './common/conditions-of-sale'

const { Text, Paragraph } = Typography

const AUCTION_IDS_WITHOUT_STAR_VENUE = [925, 928, 929, 940, 941, 942]
const PUMP_LOTS_IDS_CONDITIONS_OF_SALE_LINK = [
  19009, 18997, 18998, 18999, 19000, 19001, 19002, 19003, 19004, 19006, 19007, 19008, 19010, 19011,
  19546, 19555, 19557, 19615,
]

const AuctionLayout: NextPage<{
  artworkNode: GetEventAuctionByIdQuery['findArtwork']
  loading?: boolean
  salesAs: 'AUCTION' | 'GIVEAWAY'
}> = ({ artworkNode, loading, salesAs }) => {
  const {
    query: { slug },
  } = useQueryParams()

  const {
    data: user,
    loading: userLoading,
    refetch: refetchUserOffers,
  } = useQuery<UserOffersQuery>(GET_USER_OFFERS, {
    variables: {
      artwork: artworkNode?.id,
    },
    notifyOnNetworkStatusChange: true,
  })

  const {
    data: findArtwork,
    loading: auctionLoading,
    refetch: refetchAuctionData,
  } = useQuery<AuctionByArtworkQuery, AuctionByArtworkQueryVariables>(GET_AUCTION_BY_ARTWORK, {
    variables: {
      pk: Number(artworkNode?.pk),
    },
  })
  const auction = findArtwork?.auction
  const pusherSlug = salesAs === 'AUCTION' ? slug : ''

  const { dataIsOutOfDateState, setDataIsOutOfDateState, pusherData } = usePusher(pusherSlug)

  const someOneOutbidded = user?.currentUser?.offers?.edges[0]?.node.maxBidAmount < pusherData.bid

  useEffect(() => {
    if (dataIsOutOfDateState && someOneOutbidded) {
      notifyError(
        'Someone has set their max bid higher than your current bid. Increase your bid to stay in the race.'
      )
    }
    refetchUserOffers()
    setDataIsOutOfDateState(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataIsOutOfDateState])

  useEffect(() => {
    // the data is cached, need to refetch after confirming the bid
    refetchAuctionData()
  }, [])

  const imageSrc = artworkNode?.artworkOrTourOrArtistImage

  const auctionWithoutVenue = { ...artworkNode, venue: undefined }

  const metaData = AUCTION_IDS_WITHOUT_STAR_VENUE.includes(+artworkNode.pk)
    ? auctionWithoutVenue
    : artworkNode

  const images = artworkNode?.images.edges
    .map(({ node }) => ({
      url: getImageUrl(node, 560, 420),
      key: node.id,
    }))
    .reverse()

  const thumbnails = artworkNode?.images.edges
    .map(({ node }) => ({
      url: getImageUrl(node, 44, 44),
      key: node.id,
    }))
    .reverse()
  const meta = auction && JSON.parse(auction?.meta)

  const descArticles = artworkNode.description
    ? [
        {
          title: 'About the experience',
          description: artworkNode.description,
          key: 'descArticles',
        },
      ]
    : []

  const deliveryNotes = auction?.deliveryNotes
    ? [
        {
          title: 'Delivery notes',
          description: auction?.deliveryNotes,
          key: 'deliveryNotes',
        },
      ]
    : []

  const venue = artworkNode?.venue
  const eventStartDate =
    artworkNode?.eventStartDateLocal && formatDateTime(artworkNode?.eventStartDateLocal, 'long')

  return (
    <>
      <MetaTags
        {...getEventMetaData({ ...metaData, imageSrc })}
        schema={
          auctionWithoutVenue.eventStartDateLocal ? getEventSchema({ ...metaData, imageSrc }) : null
        }
      />
      <TwoColumnLayout>
        <Grid gap={40}>
          {artworkNode?.images.edges.length > 1 ? (
            <SlideShowGallery
              images={images}
              thumbnail={{
                images: thumbnails,
                size: {
                  width: 44,
                  height: 44,
                },
              }}
            />
          ) : (
            <img
              src={getImageUrl(imageSrc, 900, 600)}
              className="aspect-3/2 object-cover"
              alt={artworkNode.title}
            />
          )}
        </Grid>

        <Grid gap={20} className={styles['auction-info-bid']}>
          <Grid gap={10}>
            {artworkNode.title && (
              <GlowHeading Tag="h2" size="h3">
                {artworkNode.title}
              </GlowHeading>
            )}
            {auction?.subTitle && <Paragraph color="g-gray-1">{auction?.subTitle}</Paragraph>}

            {(venue || eventStartDate) && (
              <Grid>
                {venue && venue.name !== 'Online experience' && (
                  <>
                    <Text color="g-white">{venue.name}</Text>
                    <Text color="g-white">{`${venue.city}, ${venue.state}`}</Text>
                  </>
                )}
                {eventStartDate && <Text color="g-white">{eventStartDate}</Text>}
              </Grid>
            )}

            <EditLink entityType="AUCTION" id={artworkNode?.id} />
          </Grid>

          <TimeLeft
            artworkNode={artworkNode}
            loading={loading || auctionLoading}
            auction={auction}
            prefix={textToTitleCase(salesAs)}
          />

          {salesAs === 'GIVEAWAY' && (
            <SubmitForGiveway
              userData={user}
              auction={auction}
              loading={loading || userLoading || auctionLoading}
              artworkId={artworkNode?.id}
              termsAndConditionsUrl={meta?.giveaway?.terms}
            />
          )}

          {salesAs === 'AUCTION' && (
            <>
              <CurrentBidContainer
                userData={user}
                artworkNode={artworkNode}
                auction={auction}
                pusherData={pusherData}
                loading={loading || userLoading || auctionLoading}
              />

              <PlaceBid
                userData={user}
                auction={auction}
                pusherData={pusherData}
                loading={loading || userLoading || auctionLoading}
              />
            </>
          )}
        </Grid>

        <Tabs.Tabs defaultActiveKey="1">
          <Tabs.Tab name={salesAs === 'AUCTION' ? 'Lot Details' : 'Giveaway Details'} key="1">
            <Grid paddingTop={52} gap={52}>
              {Array.isArray(meta?.opensea) && meta?.opensea?.length > 0 && (
                <Grid gridTemplateColumns="repeat(auto-fill, minmax(240px, 1fr))" gap="1rem">
                  {meta.opensea.map(({ contractAddress, tokenId }) => (
                    <OpenSeaAssetCard
                      key={tokenId}
                      contactAddress={contractAddress}
                      tokenId={tokenId}
                    />
                  ))}
                </Grid>
              )}

              <DescBlock articles={descArticles} />
            </Grid>
          </Tabs.Tab>

          {salesAs !== 'GIVEAWAY' && (
            <Tabs.Tab name="Conditions of Sale" key="2">
              <Grid paddingTop={52} gap={0}>
                {PUMP_LOTS_IDS_CONDITIONS_OF_SALE_LINK.includes(Number(auction?.pk)) ? (
                  <Grid padding="0 0 20px 20px">
                    <Link
                      href="https://support.granted.co/hc/en-us/articles/8334580127892-Granted-Pump-Foundation-Auction-Terms"
                      external
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Click here to view the conditions of sale for this lot.
                    </Link>
                  </Grid>
                ) : (
                  <ConditionsOfSale />
                )}
              </Grid>
            </Tabs.Tab>
          )}

          <Tabs.Tab name="Delivery" key="3">
            <Grid paddingTop={52} gap={52}>
              <DescBlock articles={deliveryNotes} withoutAbout />
            </Grid>
          </Tabs.Tab>
        </Tabs.Tabs>
      </TwoColumnLayout>
    </>
  )
}

export default AuctionLayout
